import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import EmailStep from "./EmailStep";
import OtpStep from "./OtpStep";
import PasswordStep from "./PasswordStep";
import CreatePasswordStep from "./CreatePasswordStep";
import ForgotPasswordStep from "./ForgotPasswordSwitch";

import {
  createLoginPassword,
  createTripByName,
  getLoginStatus,
  loginWithPassword,
  requestForgotPasswordOtp,
  resetForgotPassword,
  sendOtp,
  skipLoginOtp,
  verifyEmailAndSendOtp,
  verifyGeneratedPassword,
  verifyForgotPasswordOtp,
  verifyLoginOtp,
  verifyUser,
} from "../../api/apiService";
import { useSnackBar } from "../snackBar/SnackBarContext";

import InfoTextHighlight from "../InfoTextHighlight";
const STEP = {
  EMAIL: "EMAIL",
  PASSWORD: "PASSWORD",
  NEW_USER_OTP: "NEW_USER_OTP",
  CREATE_PASSWORD: "CREATE_PASSWORD",
  FORGOT_SWITCH: "FORGOT_SWITCH",
  FORGOT_OTP: "FORGOT_OTP",
  FORGOT_ADMIN_PASSWORD: "FORGOT_ADMIN_PASSWORD",
  RESET_PASSWORD: "RESET_PASSWORD",
};

const CREATE_PASSWORD_ORIGIN = {
  NEW_USER_OTP: "NEW_USER_OTP",
  FORCE_PASSWORD: "FORCE_PASSWORD",
  FORGOT_ADMIN: "FORGOT_ADMIN",
  UNKNOWN: "UNKNOWN",
};

const extractMessage = (
  error,
  fallback = "Something went wrong. Please try again.",
) => {
  if (Array.isArray(error) && error.length > 0 && error[0]?.message) {
    return error[0].message;
  }

  const apiData = error?.response?.data;
  if (Array.isArray(apiData) && apiData.length > 0 && apiData[0]?.message) {
    return apiData[0].message;
  }

  if (apiData?.message) {
    return apiData.message;
  }

  if (error?.message) {
    return error.message;
  }

  return fallback;
};

const getPayload = (response) => response?.data || response || {};
const isStrongPassword = (password) => {
  if (!password || password.length < 8) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[^A-Za-z0-9]/.test(password)) return false;
  return true;
};
const isFailureMessage = (message) => {
  if (!message || typeof message !== "string") return false;
  const text = message.toLowerCase();
  return (
    text.includes("failed") ||
    text.includes("not sent") ||
    text.includes("unable") ||
    text.includes("error")
  );
};

const AuthContainer = ({
  tripName,
  onStepChange,
  onVerifyEmailBannerChange,
  backSignal = 0,
}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackBar();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authStatus, setAuthStatus] = useState(null);
  const [otpSeconds, setOtpSeconds] = useState(null);
  const [showReminder, setShowReminder] = useState(false);
  const [isGeneratedPasswordVerified, setIsGeneratedPasswordVerified] =
    useState(false);
  const [isForceFlowPasswordCreated, setIsForceFlowPasswordCreated] =
    useState(false);
  const [isForgotAdminVerified, setIsForgotAdminVerified] = useState(false);
  const [otpFlow, setOtpFlow] = useState("NEW_USER");
  const [forgotOtpInitialized, setForgotOtpInitialized] = useState(false);
  const [createPasswordOrigin, setCreatePasswordOrigin] = useState(
    CREATE_PASSWORD_ORIGIN.UNKNOWN,
  );

  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step, onStepChange]);

  const goBackByStep = () => {
    if (step === STEP.PASSWORD) {
      setStep(STEP.EMAIL);
      return;
    }

    if (step === STEP.FORGOT_SWITCH) {
      setStep(STEP.PASSWORD);
      return;
    }

    if (step === STEP.NEW_USER_OTP) {
      setStep(STEP.EMAIL);
      return;
    }

    if (step === STEP.FORGOT_OTP) {
      setStep(STEP.FORGOT_SWITCH);
      return;
    }

    if (step === STEP.FORGOT_ADMIN_PASSWORD) {
      setStep(STEP.FORGOT_SWITCH);
      return;
    }

    if (step === STEP.RESET_PASSWORD) {
      setStep(STEP.FORGOT_SWITCH);
      return;
    }

    if (step === STEP.CREATE_PASSWORD) {
      if (createPasswordOrigin === CREATE_PASSWORD_ORIGIN.NEW_USER_OTP) {
        setOtpFlow("NEW_USER");
        setStep(STEP.NEW_USER_OTP);
        return;
      }

      if (createPasswordOrigin === CREATE_PASSWORD_ORIGIN.FORGOT_ADMIN) {
        setStep(STEP.FORGOT_ADMIN_PASSWORD);
        return;
      }

      setStep(STEP.PASSWORD);
    }
  };

  useEffect(() => {
    if (!backSignal) return;
    goBackByStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backSignal]);

  useEffect(() => {
    if (otpSeconds == null || otpSeconds <= 0) return;
    const timer = setInterval(() => {
      setOtpSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpSeconds]);

  const isForcePasswordChange = authStatus?.forcePasswordChange === "yes";
  const isForgotOtpExpired = otpFlow === "FORGOT_OTP" && otpSeconds === 0;
  const shouldShowVerifyEmailLink =
    authStatus?.isVerified === "no" && authStatus?.nextStep === "PROCEED";

  useEffect(() => {
    onVerifyEmailBannerChange?.(step === STEP.PASSWORD && shouldShowVerifyEmailLink);
  }, [onVerifyEmailBannerChange, step, shouldShowVerifyEmailLink]);

  const infoTextByStep = useMemo(() => {
    if (step === STEP.NEW_USER_OTP) {
      if (authStatus?.otpStatus === "VERIFIED") {
        return "OTP verified successfully.";
      }

      return (
        <span>
          Please <span className="text-secondary">verify OTP</span> to secure
          your account. If <span className="text-secondary">OTP</span> is not
          received, you can <span className="text-secondary">skip</span> now and
          come back later.
        </span>
      );
    }

    if (step === STEP.PASSWORD && isForcePasswordChange) {
      return (
        <>
          Sorry for the inconvenience. Contact the admin at{" "}
          <a
            href="tel:+18604022247" // International format without spaces
            className="text-secondary underline"
          >
            +1 8604022247
          </a>{" "}
          for a Generated password. If you already have one, enter it here, then{" "}
          <span className="text-secondary">create new password</span> and
          confirm to continue.
        </>
      );
    }

    return "";
  }, [step, authStatus, isForcePasswordChange]);

  const completeLogin = (response) => {
    const result = getPayload(response);
    const isEuropeTrip = tripName?.toLowerCase().startsWith("europe");
    const route = isEuropeTrip ? "/europregister" : "/register";
    const isNewUser = !result?.personalInfoDTO;

    localStorage.setItem("email", email);
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("isNewUser", String(isNewUser));

    if (result?.personalInfoDTO?.isInvited != null) {
      localStorage.setItem("userType", result.personalInfoDTO.isInvited);
    }

    if (tripName) {
      sessionStorage.setItem("tripName", tripName);
    }

    navigate(route, {
      state: {
        infoMessage:
          result?.participantExistsAnotherTrip === "yes"
            ? "As an existing registered user, your personal information will be seamlessly imported from your profile."
            : null,
      },
    });
  };

  const hydrateStatus = async (userEmail) => {
    const statusRes = await getLoginStatus(userEmail);
    const statusData = getPayload(statusRes);
    setAuthStatus(statusData);
    setOtpSeconds(
      Number.isFinite(statusData?.otpExpiresInSeconds)
        ? statusData.otpExpiresInSeconds
        : null,
    );
    return statusData;
  };

  const handleEmailVerify = async () => {
    setError("");

    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError("Enter a valid email (e.g., user@example.com)");
      return;
    }

    setLoading(true);

    try {
      if (tripName) {
        await createTripByName(tripName);
        sessionStorage.setItem("tripName", tripName);
      }

      try {
        await verifyUser(email, tripName);
      } catch (checkError) {
        const checkMessage = extractMessage(checkError, "");
        const isEmailNotExists =
          typeof checkMessage === "string" &&
          checkMessage.toLowerCase().includes("email does not exist");

        if (!isEmailNotExists) {
          throw checkError;
        }
      }

      const createRes = await sendOtp(email);
      const createData = getPayload(createRes);
      setAuthStatus(createData);
      setIsGeneratedPasswordVerified(false);
      setIsForceFlowPasswordCreated(false);
      setIsForgotAdminVerified(false);
      setOtpFlow("NEW_USER");
      setForgotOtpInitialized(false);
      setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.UNKNOWN);

      if (Number.isFinite(createData?.otpExpiresInSeconds)) {
        setOtpSeconds(createData.otpExpiresInSeconds);
      }
      const createMessage = createRes?.message || createData?.message || "";
      const otpSendFailed = isFailureMessage(createMessage);

      const latest = await hydrateStatus(email);
      localStorage.setItem("email", email);
      localStorage.setItem("isLoggedIn", "false");
      localStorage.setItem("isNewUser", "false");

      let nextStep = STEP.PASSWORD;
      if (
        latest?.passwordStatus === "NOT_SET" &&
        latest?.isVerified === "no" &&
        (latest?.otpStatus === "PENDING" || latest?.otpStatus === "SKIPPED")
      ) {
        setOtpFlow("NEW_USER");
        nextStep = STEP.NEW_USER_OTP;
      } else if (latest?.forcePasswordChange === "yes") {
        nextStep = STEP.PASSWORD;
      } else if (createData?.nextStep === "LOGIN") {
        nextStep = STEP.PASSWORD;
      } else if (latest?.passwordStatus === "NOT_SET") {
        if (
          latest?.otpStatus === "VERIFIED" ||
          latest?.otpStatus === "SKIPPED"
        ) {
          setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.NEW_USER_OTP);
          nextStep = STEP.CREATE_PASSWORD;
        } else {
          setOtpFlow("NEW_USER");
          nextStep = STEP.NEW_USER_OTP;
        }
      } else {
        nextStep = STEP.PASSWORD;
      }

      setStep(nextStep);

      if (createMessage) {
        const suppressForcePasswordSnackbar =
          latest?.forcePasswordChange === "yes" && nextStep === STEP.PASSWORD;

        if (suppressForcePasswordSnackbar) {
          // No snackbar needed for generated-password guidance; message is shown in UI.
        } else if (otpSendFailed) {
          showSnackbar("error", createMessage);
          if (nextStep === STEP.NEW_USER_OTP) {
            setError(createMessage);
          }
        } else {
          showSnackbar("success", createMessage);
          if (nextStep === STEP.NEW_USER_OTP) {
            setError("");
          }
        }
      }
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (password) => {
    setError("");
    if (!password?.trim()) {
      setError("Password is required.");
      return;
    }

    setLoading(true);

    try {
      if (isForcePasswordChange && !isForceFlowPasswordCreated) {
        const verifyResponse = await verifyGeneratedPassword(
          email,
          password,
          tripName,
        );
        if (verifyResponse?.status !== 200) {
          setError("Generated password verification failed. Please try again.");
          return;
        }

        setIsGeneratedPasswordVerified(true);
        setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.FORCE_PASSWORD);
        setStep(STEP.CREATE_PASSWORD);
        showSnackbar(
          "info",
          "Please create and confirm your new password to continue.",
        );
      } else {
        const response = await loginWithPassword(email, password, tripName);
        completeLogin(response);
      }
    } catch (err) {
      const message = extractMessage(
        err,
        "Invalid password. Please try again.",
      );
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNewUserOtp = async () => {
    setError("");
    if (!otp || otp.length !== 6) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);
    try {
      const verifyRes = await verifyLoginOtp(email, otp);
      const verifyData = getPayload(verifyRes);
      setAuthStatus(verifyData);
      if (otpFlow === "VERIFY_EMAIL") {
        setStep(STEP.PASSWORD);
        showSnackbar(
          "success",
          verifyRes?.message || "Email verified successfully.",
        );
      } else {
        setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.NEW_USER_OTP);
        setStep(STEP.CREATE_PASSWORD);
        showSnackbar(
          "success",
          verifyRes?.message || "OTP verified successfully.",
        );
      }
    } catch (err) {
      const message = extractMessage(err, "OTP verification failed.");
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipNewUserOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const skipRes = await skipLoginOtp(email);
      const skipData = getPayload(skipRes);
      setAuthStatus(skipData);
      setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.NEW_USER_OTP);
      setStep(STEP.CREATE_PASSWORD);
      showSnackbar(
        "info",
        skipRes?.message || "OTP skipped. You can verify later.",
      );
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendNewUserOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const resendRes =
        otpFlow === "VERIFY_EMAIL"
          ? await verifyEmailAndSendOtp(email)
          : await sendOtp(email);
      const resendData = getPayload(resendRes);
      setAuthStatus((prev) => ({ ...prev, ...resendData }));
      setOtpSeconds(
        Number.isFinite(resendData?.otpExpiresInSeconds)
          ? resendData.otpExpiresInSeconds
          : otpSeconds,
      );
      const resendMessage =
        resendRes?.message || resendData?.message || "OTP sent to your email.";
      if (isFailureMessage(resendMessage)) {
        setError(resendMessage);
        showSnackbar("error", resendMessage);
      } else {
        setError("");
        showSnackbar("success", resendMessage);
      }
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailFromPassword = async () => {
    setError("");
    setLoading(true);
    try {
      const resendRes = await verifyEmailAndSendOtp(email);
      const resendData = getPayload(resendRes);
      setAuthStatus((prev) => ({ ...prev, ...resendData }));
      setOtp("");
      setOtpFlow("VERIFY_EMAIL");
      setOtpSeconds(
        Number.isFinite(resendData?.otpExpiresInSeconds)
          ? resendData.otpExpiresInSeconds
          : otpSeconds,
      );
      setStep(STEP.NEW_USER_OTP);
      const resendMessage =
        resendRes?.message ||
        resendData?.message ||
        "OTP sent. Please verify your email.";
      if (isFailureMessage(resendMessage)) {
        setError(resendMessage);
        showSnackbar("error", resendMessage);
      } else {
        setError("");
        showSnackbar("success", resendMessage);
      }
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePassword = async (newPassword, confirmPassword) => {
    setError("");
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required.");
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError(
        "Password must be at least 8 characters and include one uppercase letter, one number, and one special character.",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const createRes =
        step === STEP.RESET_PASSWORD
          ? await resetForgotPassword(email, newPassword, confirmPassword)
          : await createLoginPassword(email, newPassword, confirmPassword);

      const latest = await hydrateStatus(email);
      setAuthStatus(latest);
      setShowReminder(true);
      showSnackbar(
        "success",
        createRes?.message || "Password created successfully.",
      );

      if (isForcePasswordChange && isGeneratedPasswordVerified) {
        setIsForceFlowPasswordCreated(true);
        setIsGeneratedPasswordVerified(false);
        setStep(STEP.PASSWORD);
        showSnackbar(
          "info",
          "Please login with your new password to continue.",
        );
        return;
      }

      if (isForgotAdminVerified) {
        setIsForgotAdminVerified(false);
        setStep(STEP.PASSWORD);
        showSnackbar(
          "info",
          "Please login with your new password to continue.",
        );
        return;
      }

      setStep(STEP.PASSWORD);
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRequest = async ({ keepSwitch = false } = {}) => {
    setError("");
    if (!email) {
      setError("Enter email before using forgot password.");
      return;
    }

    setLoading(true);
    try {
      const requestRes = await requestForgotPasswordOtp(email);
      const requestData = getPayload(requestRes);
      setAuthStatus(requestData);
      setOtpSeconds(
        Number.isFinite(requestData?.otpExpiresInSeconds)
          ? requestData.otpExpiresInSeconds
          : null,
      );
      setOtp("");
      setOtpFlow("FORGOT_OTP");
      setForgotOtpInitialized(true);
      if (!keepSwitch) {
        setStep(STEP.FORGOT_OTP);
      }
      showSnackbar(
        "info",
        requestRes?.message || "OTP sent to your registered email.",
      );
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyForgotOtp = async () => {
    setError("");
    if (!otp || otp.length !== 6) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    if (isForgotOtpExpired) {
      setError("OTP expired. Please contact Admin for Generated password.");
      return;
    }

    setLoading(true);
    try {
      const verifyRes = await verifyForgotPasswordOtp(email, otp);
      const verifyData = getPayload(verifyRes);
      setAuthStatus(verifyData);
      setStep(STEP.RESET_PASSWORD);
      showSnackbar("success", verifyRes?.message || "OTP verified.");
    } catch (err) {
      const message = extractMessage(err);
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendForgotOtp = async () => {
    await handleForgotPasswordRequest({ keepSwitch: true });
  };

  const handleForgotAdminGeneratedPasswordVerify = async (password) => {
    setError("");
    if (!password?.trim()) {
      setError("Generated password is required.");
      return;
    }

    setLoading(true);
    try {
      const verifyResponse = await verifyGeneratedPassword(
        email,
        password,
        tripName,
      );
      if (verifyResponse?.status !== 200) {
        setError("Generated password verification failed. Please try again.");
        return;
      }

      setIsForgotAdminVerified(true);
      setCreatePasswordOrigin(CREATE_PASSWORD_ORIGIN.FORGOT_ADMIN);
      setStep(STEP.CREATE_PASSWORD);
      showSnackbar(
        "success",
        "Generated password verified. Please create your new password.",
      );
    } catch (err) {
      const message = extractMessage(
        err,
        "Generated password verification failed.",
      );
      setError(message);
      showSnackbar("error", message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (step === STEP.EMAIL) {
      return (
        <EmailStep
          email={email}
          step={step}
          setEmail={setEmail}
          onVerify={handleEmailVerify}
          loading={loading}
          error={error}
          setError={setError}
          // infoText="Enter your email to continue with verification and password setup."
          infoText={<InfoTextHighlight />}
        />
      );
    }

    if (step === STEP.PASSWORD) {
      return (
        <PasswordStep
          step={step}
          email={email}
          title={
            isForcePasswordChange
              ? "Enter Generated Password"
              : "Login With Password"
          }
          description={infoTextByStep}
          submitLabel={isForcePasswordChange ? "Verify" : "Sign In"}
          inlineSubmit={!isForcePasswordChange}
          onSubmit={handlePasswordLogin}
          onVerifyEmail={handleVerifyEmailFromPassword}
          showVerifyEmail={shouldShowVerifyEmailLink}
          onForgot={() => {
            setError("");
            setOtp("");
            setForgotOtpInitialized(false);
            setOtpFlow("FORGOT_OTP");
            setStep(STEP.FORGOT_SWITCH);
          }}
          onBack={goBackByStep}
          loading={loading}
          setError={setError}
          error={error}
          isForcePasswordChange={isForcePasswordChange}
        />
      );
    }

    if (step === STEP.FORGOT_SWITCH) {
      return (
        <ForgotPasswordStep
          step={step}
          email={email}
          onBack={() => setStep(STEP.PASSWORD)}
          otp={otp}
          setOtp={setOtp}
          seconds={otpSeconds}
          loading={loading}
          error={error}
          setError={setError}
          otpInitialized={forgotOtpInitialized}
          otpExpired={isForgotOtpExpired}
          onOtpInit={() => handleForgotPasswordRequest({ keepSwitch: true })}
          onOtpVerify={handleVerifyForgotOtp}
          onOtpResend={handleResendForgotOtp}
          onAdminVerify={handleForgotAdminGeneratedPasswordVerify}
          onAdmin={() => {
            setError("");
            setStep(STEP.FORGOT_ADMIN_PASSWORD);
          }}
        />
      );
    }

    if (step === STEP.NEW_USER_OTP) {
      return (
        <OtpStep
          step={step}
          email={email}
          title="Verify OTP"
          otp={otp}
          setOtp={setOtp}
          setError={setError}
          onVerify={handleVerifyNewUserOtp}
          onBack={goBackByStep}
          onSkip={handleSkipNewUserOtp}
          onResend={handleResendNewUserOtp}
          seconds={otpSeconds}
          error={error}
          loading={loading}
          allowSkip={otpFlow === "NEW_USER"}
          isVerifyEmailFlow={otpFlow === "VERIFY_EMAIL"}
          infoText={infoTextByStep}
        />
      );
    }

    if (step === STEP.FORGOT_OTP) {
      return (
        <OtpStep
          step={step}
          title="Forgot Password OTP Verification"
          otp={otp}
          setOtp={setOtp}
          onVerify={handleVerifyForgotOtp}
          onBack={goBackByStep}
          onResend={handleResendForgotOtp}
          seconds={otpSeconds}
          error={error}
          loading={loading}
          expired={isForgotOtpExpired}
          expiryMessage="OTP expired or not received? Please contact Admin for Generated password."
          infoText="Verify OTP within the given time to reset your password."
        />
      );
    }

    if (step === STEP.FORGOT_ADMIN_PASSWORD) {
      return (
        <PasswordStep
          setError={setError}
          title="Enter Generated Password"
          description="Get the generated password from admin/support and verify it to continue."
          submitLabel="Verify Generated Password"
          onSubmit={handleForgotAdminGeneratedPasswordVerify}
          onBack={goBackByStep}
          loading={loading}
          error={error}
        />
      );
    }

    if (step === STEP.CREATE_PASSWORD || step === STEP.RESET_PASSWORD) {
      return (
        <CreatePasswordStep
          step={step}
          title="Create and Confirm New Password"
          description="After creating your password, please log in to continue with your registration."
          submitLabel={
            step === STEP.RESET_PASSWORD ? "Reset Password" : "Create Password"
          }
          onSubmit={handleCreatePassword}
          onBack={goBackByStep}
          loading={loading}
          error={error}
          setError={setError}
        />
      );
    }

    return null;
  };

  return (
    <div className="pt-6">
      {renderStep()}

      {showReminder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-5 max-w-md w-full text-center">
            <CheckCircle2
              size={34}
              className="mx-auto text-green-600"
              aria-hidden="true"
            />
            <h3 className="text-base font-semibold pt-2">
              Password created successfully
            </h3>
            <p className="text-sm text-primary pt-2">
              Please remember this password for future logins. Keep it private
              and secure.
            </p>
            <button
              type="button"
              onClick={() => setShowReminder(false)}
              className="mt-4 bg-buttonColor text-white text-xs px-4 py-2 rounded-md"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthContainer;
